import { useState, useCallback } from 'react';

export interface UseMutationOptions<TData, TError = Error, TVariables = void> {
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
  onSettled?: (data: TData | undefined, error: TError | null, variables: TVariables) => void;
  onMutate?: (variables: TVariables) => void;
}

export interface UseMutationResult<TData, TError = Error, TVariables = void> {
  data: TData | undefined;
  error: TError | null;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  mutate: (variables: TVariables) => Promise<void>;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  reset: () => void;
}

export function useMutation<TData = unknown, TError = Error, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: UseMutationOptions<TData, TError, TVariables> = {}
): UseMutationResult<TData, TError, TVariables> {
  const { onSuccess, onError, onSettled, onMutate } = options;

  const [data, setData] = useState<TData | undefined>(undefined);
  const [error, setError] = useState<TError | null>(null);
  const [isPending, setIsPending] = useState<boolean>(false);

  const reset = useCallback(() => {
    setData(undefined);
    setError(null);
    setIsPending(false);
  }, []);

  const mutateAsync = useCallback(
    async (variables: TVariables): Promise<TData> => {
      setIsPending(true);
      setError(null);

      try {
        onMutate?.(variables);

        const result = await mutationFn(variables);
        setData(result);
        setError(null);

        onSuccess?.(result, variables);
        onSettled?.(result, null, variables);

        return result;
      } catch (err) {
        const mutationError = err as TError;
        setError(mutationError);
        setData(undefined);
        
        onError?.(mutationError, variables);
        onSettled?.(undefined, mutationError, variables);
        
        throw mutationError;
      } finally {
        setIsPending(false);
      }
    },
    [mutationFn, onSuccess, onError, onSettled, onMutate]
  );

  const mutate = useCallback(
    async (variables: TVariables): Promise<void> => {
      try {
        await mutateAsync(variables);
      } catch (err) {
        // Error is already handled in mutateAsync
      }
    },
    [mutateAsync]
  );

  return {
    data,
    error,
    isPending,
    isError: error !== null,
    isSuccess: data !== undefined && error === null,
    mutate,
    mutateAsync,
    reset,
  };
}
